import { computed } from 'vue';
import { useMobile } from './useMobile';

const MOBILE_ITEMS_PER_PAGE = 10;

// Single source of truth for making a v-data-table usable on phones: spread
// `tableProps` onto the table to stack rows into cards and paginate at 10/page
// below 960px, while desktop keeps `desktopItemsPerPage` (default -1 = all rows).
// Remove any explicit items-per-page / hide-default-footer / empty #bottom slot
// so these win.
//
//   const { tableProps } = useResponsiveTable({ desktopItemsPerPage: 25 });
//   <v-data-table v-bind="tableProps" :headers="headers" :items="items">
//
// Pass `stack: false` for tables whose custom layout (group-by, raw #expanded-row,
// scheduling grids) breaks when auto-stacked: they keep horizontal scroll but
// still paginate at 10/page on mobile.
//
// Pass `alwaysShowFooter: true` for tables that show all rows (-1) but still need
// the footer's row counter (and arrows) on desktop, e.g. so users can read the
// total number of rows.
export function useResponsiveTable(
  options: { desktopItemsPerPage?: number; stack?: boolean; alwaysShowFooter?: boolean } = {},
) {
  const { desktopItemsPerPage = -1, stack = true, alwaysShowFooter = false } = options;
  const { isMobile } = useMobile();

  const tableProps = computed(() => ({
    mobile: stack ? isMobile.value : false,
    itemsPerPage: isMobile.value ? MOBILE_ITEMS_PER_PAGE : desktopItemsPerPage,
    // pager shows on mobile (10/page) and on desktop when paginating; with
    // alwaysShowFooter it also stays on desktop while showing all rows so the
    // total row count remains visible.
    hideDefaultFooter: isMobile.value ? false : (!alwaysShowFooter && desktopItemsPerPage === -1),
  }));

  return { tableProps };
}
